import { useState, useEffect } from "react";
import axios from "axios";
import cacheStorage from "../../../utils/cacheStorage";
import { ArrowLeft, Navigation, Phone, MapPin, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCourierLanguage } from "../contexts/CourierLanguageContext";

import { MapContainer, TileLayer, Marker, Polyline, useMap, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// Fix for default Leaflet marker icons in React
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Helper component to auto-center the map around our delivery points
function MapUpdater({ coordinates }) {
  const map = useMap();
  useEffect(() => {
    const validCoords = coordinates.filter(
        c => c && typeof c[0] === 'number' && !isNaN(c[0]) && typeof c[1] === 'number' && !isNaN(c[1])
    );
    if (validCoords.length > 0) {
      const bounds = L.latLngBounds(validCoords);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [coordinates, map]);
  return null;
}

// NEW: Helper function to calculate straight-line distance between two GPS coordinates
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

export function ActiveRoute({ navigateTo }) {
  const { t } = useCourierLanguage();

  const [deliveries, setDeliveries] = useState([]);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [liveLocation, setLiveLocation] = useState(null);
  const [streetPath, setStreetPath] = useState([]);

  // NEW: State to track if we have successfully sorted the route
  const [hasOptimizedRoute, setHasOptimizedRoute] = useState(false);

  // 1. Fetch the active routes from the database
  useEffect(() => {
    const fetchDeliveries = async () => {
      try {
        setIsLoading(true);
        const token = cacheStorage.getItem('access_token');
        const response = await axios.get("https://smart-cargo-backend-923182991420.us-central1.run.app/api/courier/deliveries", {
          headers: token ? { "Authorization": `Bearer ${token}` } : {}
        });

        if (response.data) {
          const validDeliveries = response.data.map((pkg, index) => {
            const fallbackLat = 40.3756 + (index * 0.002);
            const fallbackLng = 49.8465 + (index * 0.002);
            let lat = pkg.delivery_location_x;
            let lng = pkg.delivery_location_y;
            if (lat && typeof lat === 'object') lat = lat.Valid ? lat.Float64 : null;
            if (lng && typeof lng === 'object') lng = lng.Valid ? lng.Float64 : null;

            return {
              ...pkg,
              delivery_location_x: (lat !== null && lat !== undefined) ? Number(lat) : fallbackLat,
              delivery_location_y: (lng !== null && lng !== undefined) ? Number(lng) : fallbackLng
            };
          });
          setDeliveries(validDeliveries);
        }
      } catch (error) {
        console.error("Error fetching deliveries:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDeliveries();
  }, []);

  // 2. Get Live Location using Geolocation API
  useEffect(() => {
    if ("geolocation" in navigator) {
      const watchId = navigator.geolocation.watchPosition(
          (position) => {
            setLiveLocation([position.coords.latitude, position.coords.longitude]);
          },
          (error) => console.error("Error getting live location:", error),
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, []);

  // 3. NEW: Optimize the sequence of deliveries (Greedy Nearest Neighbor)
  useEffect(() => {
    if (!liveLocation || deliveries.length === 0 || hasOptimizedRoute) return;

    let currentLoc = { lat: liveLocation[0], lon: liveLocation[1] };
    let unvisited = [...deliveries];
    let optimized = [];

    // Loop through and constantly pick the closest next stop
    while (unvisited.length > 0) {
      let nearestIdx = 0;
      let minDist = Infinity;

      for (let i = 0; i < unvisited.length; i++) {
        const d = unvisited[i];
        const dist = calculateDistance(currentLoc.lat, currentLoc.lon, d.delivery_location_x, d.delivery_location_y);
        if (dist < minDist) {
          minDist = dist;
          nearestIdx = i;
        }
      }

      const nearest = unvisited[nearestIdx];
      optimized.push(nearest);
      currentLoc = { lat: nearest.delivery_location_x, lon: nearest.delivery_location_y };
      unvisited.splice(nearestIdx, 1); // Remove from unvisited pool
    }

    setDeliveries(optimized); // Update UI with optimal order
    setHasOptimizedRoute(true); // Ensure we only sort once so the route stays stable!
  }, [liveLocation, deliveries, hasOptimizedRoute]);

  // 4. Fetch Street Route from Go Backend (Only after optimization)
  useEffect(() => {
    const fetchRoute = async () => {
      // Don't fetch until the route is perfectly sorted
      if (!liveLocation || deliveries.length === 0 || !hasOptimizedRoute) return;

      try {
        const remainingDeliveries = deliveries.slice(currentStopIndex);
        const requestPoints = [
          { lat: liveLocation[0], lon: liveLocation[1] },
          ...remainingDeliveries.map(d => ({
            lat: d.delivery_location_x,
            lon: d.delivery_location_y
          }))
        ];

        const token = cacheStorage.getItem('access_token');
        const response = await axios.post("https://smart-cargo-backend-923182991420.us-central1.run.app/api/route", requestPoints, {
          headers: token ? { "Authorization": `Bearer ${token}` } : {}
        });

        if (response.data && response.data.length > 0) {
          const leafletPath = response.data.map(p => [p.lat, p.lon]);
          setStreetPath(leafletPath);
        }
      } catch (error) {
        console.error("Error fetching street route:", error);
      }
    };

    fetchRoute();
    // Intentionally ignoring liveLocation in the dependency array below.
    // We only want to ask the backend for a new path when a stop is completed,
    // not every single time the GPS moves 1 inch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOptimizedRoute, currentStopIndex]);

  const handleMarkDelivered = async () => {
    const currentStop = deliveries[currentStopIndex];
    try {
      const token = cacheStorage.getItem('access_token');
      await axios.put(`https://smart-cargo-backend-923182991420.us-central1.run.app/api/packages/${currentStop.id}`, {
        ...currentStop, status: "Delivered"
      }, {
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
      setShowDeliveryModal(false);
      if (currentStopIndex < deliveries.length - 1) {
        setCurrentStopIndex(currentStopIndex + 1);
      } else {
        navigateTo("dashboard");
      }
    } catch (error) {
      console.error("Failed to mark as delivered:", error);
    }
  };

  if (isLoading) {
    return <div className="h-screen flex items-center justify-center bg-white"><div className="text-xl font-semibold text---000080-">Loading route...</div></div>;
  }

  if (deliveries.length === 0) {
    return (
        <div className="h-screen flex flex-col items-center justify-center bg-white p-6">
          <CheckCircle className="w-16 h-16 text---10B981- mb-4" />
          <h2 className="text-2xl font-bold text---000080- mb-2">You're all caught up!</h2>
          <button onClick={() => navigateTo("dashboard")} className="w-full py-4 bg-neg-2D58AD- text-white rounded-xl font-semibold text-lg hover-bg---000080- mt-6">Return to Dashboard</button>
        </div>
    );
  }

  const currentStop = deliveries[currentStopIndex];

  // Include liveLocation in bounds calculation if it exists
  const allCoordinates = liveLocation
      ? [liveLocation, ...deliveries.map(d => [d.delivery_location_x, d.delivery_location_y])]
      : deliveries.map(d => [d.delivery_location_x, d.delivery_location_y]);

  return (
      <div className="h-screen flex flex-col bg-white">
        <div className="flex-1 relative bg-gradient-to-br from---DEEAFC- to---71A4F4--20">

          {/* Header */}
          <div className="absolute top-0 left-0 right-0 p-4" style={{ zIndex: 1000, pointerEvents: "none" }}>
            <div className="flex items-center justify-between">
              <button onClick={() => navigateTo("dashboard")} className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center" style={{ pointerEvents: "auto" }}>
                <ArrowLeft className="w-6 h-6 text---000080-" />
              </button>
              <div className="bg-white rounded-full px-4 py-2 shadow-lg" style={{ pointerEvents: "auto" }}>
                <p className="text-sm font-semibold text---000080-">
                  {t.stopOf?.replace("{current}", String(currentStopIndex + 1)).replace("{total}", String(deliveries.length)) || `${currentStopIndex + 1} / ${deliveries.length}`}
                </p>
              </div>
            </div>
          </div>

          <div className="absolute inset-0 z-0">
            <MapContainer center={[40.3756, 49.8465]} zoom={14} style={{ height: "100%", width: "100%" }} zoomControl={false}>
              <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapUpdater coordinates={allCoordinates} />

              {/* Display the Live Location as a pulsing blue dot */}
              {liveLocation && (
                  <CircleMarker
                      center={liveLocation}
                      radius={8}
                      pathOptions={{ color: 'white', fillColor: '#3b82f6', fillOpacity: 1, weight: 3 }}
                  />
              )}

              {/* Render Delivery Pins */}
              {deliveries.map((pkg, index) => {
                const opacity = index < currentStopIndex ? 0.4 : 1;
                return <Marker key={pkg.id} position={[pkg.delivery_location_x, pkg.delivery_location_y]} opacity={opacity} />;
              })}

              {/* Draw the actual street path returned by Go */}
              {streetPath.length > 0 && (
                  <Polyline
                      positions={streetPath}
                      color="#ef4444"
                      weight={5}
                      opacity={0.8}
                  />
              )}
            </MapContainer>
          </div>

          <div className="absolute top-20 left-4 right-4 bg-white rounded-2xl p-4 shadow-xl" style={{ zIndex: 1000 }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-neg-2D58AD--10 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-neg-2D58AD-" />
                </div>
                <div>
                  <p className="text-sm text-neg-708094-">{t.etaToNextStop || "Status"}</p>
                  <p className="text-xl font-bold text---000080-">Pending</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-neg-708094-">{t.distance || "Weight"}</p>
                <p className="text-lg font-bold text-neg-2D58AD-">{currentStop?.total_weight || 0} kg</p>
              </div>
            </div>
          </div>
        </div>

        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} className="bg-white rounded-t-3xl shadow-2xl p-6 border-t-4 border-neg-2D58AD- relative" style={{ zIndex: 1010 }}>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-neg-708094-">{t.nextDelivery || "Next Delivery"}</p>
                <p className="text-xl font-bold text---000080-">{currentStop?.tracking_code}</p>
              </div>
              <span className="px-4 py-2 bg-neg-F59E0B--10 text---F59E0B- rounded-full text-sm font-semibold">
            {t.inProgress || "In Progress"}
          </span>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-neg-2D58AD--10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-neg-2D58AD-" />
                </div>
                <div>
                  <p className="font-semibold text---000080-">
                    {typeof currentStop?.customer_name === 'object' ? currentStop?.customer_name?.String : currentStop?.customer_name}
                  </p>
                  <p className="text-sm text-neg-708094- mt-1">
                    {typeof currentStop?.delivery_address === 'object' ? currentStop?.delivery_address?.String : currentStop?.delivery_address}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mb-4">
            <a
                href={`tel:+99400000000`}
                className="flex-1 py-3 bg-white border-2 border-neg-2D58AD- text-neg-2D58AD- rounded-xl font-semibold flex items-center justify-center gap-2 hover-bg---DEEAFC- transition-colors"
            >
              <Phone className="w-5 h-5" />
              {t.call || "Call"}
            </a>
            <button className="flex-1 py-3 bg-neg-2D58AD- text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover-bg---000080- transition-colors">
              <Navigation className="w-5 h-5" />
              {t.navigate || "Navigate"}
            </button>
          </div>
          <button onClick={() => setShowDeliveryModal(true)} className="w-full py-4 bg-neg-10B981- text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 mt-4">
            <CheckCircle className="w-6 h-6" /> {t.markDelivered || "Mark Delivered"}
          </button>
        </motion.div>

        {/* CONFIRMATION MODAL */}
        <AnimatePresence>
          {showDeliveryModal && (
              <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowDeliveryModal(false)} className="fixed inset-0 bg-black-50" style={{ zIndex: 2000 }} />
                <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-w-md mx-auto" style={{ zIndex: 2010 }}>
                  <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-neg-10B981--10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text---10B981-" />
                    </div>
                    <h3 className="text-xl font-bold text---000080- mb-2">{t.confirmDelivery || "Confirm Delivery"}</h3>
                    <p className="text-neg-708094-">{currentStop?.tracking_code} bağlamasını çatdırılıb qeyd et?</p>
                  </div>
                  <div className="space-y-3">
                    <button onClick={handleMarkDelivered} className="w-full py-4 bg-neg-10B981- text-white rounded-xl font-semibold hover-bg---059669- transition-colors">{t.deliveredSuccessfully || "Mark as Delivered"}</button>
                    <button onClick={() => setShowDeliveryModal(false)} className="w-full py-4 bg-white border-2 border-gray-300 text-neg-708094- rounded-xl font-semibold hover-bg-gray-50 transition-colors">{t.cancel || "Cancel"}</button>
                    <button className="w-full py-4 bg-white border-2 border-red-500 text-red-500 rounded-xl font-semibold hover-bg-red-50 transition-colors flex items-center justify-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {t.reportIssue || "Report Issue"}
                    </button>
                  </div>
                </motion.div>
              </>
          )}
        </AnimatePresence>
      </div>
  );
}